// Heatmap: one-hue sequential ramp (light → dark), 2px surface gaps, per-cell tooltip, scale legend.
import { useState } from 'react';
import { formatValue } from '../../lib/format';
import { useSize } from '../../lib/useSize';
import type { RegistryEntry, WidgetComponentProps } from '../types';

const STEPS = ['--seq-100', '--seq-200', '--seq-300', '--seq-400', '--seq-500', '--seq-600', '--seq-700'];

function Heatmap({ data }: WidgetComponentProps) {
  const [ref, { width, height }] = useSize<HTMLDivElement>();
  const [tip, setTip] = useState<{ r: number; c: number; x: number; y: number } | null>(null);
  const matrix: (number | null)[][] = data.matrix ?? [];
  const vals = matrix.flat().filter((v): v is number => v !== null);
  const max = Math.max(1, ...vals);
  const step = (v: number | null) => (v === null || v === 0 ? 'var(--surface-2)' : `var(${STEPS[Math.min(STEPS.length - 1, Math.floor((v / max) * STEPS.length))]})`);
  const fmt = (v: number | null) => formatValue(v, data.metric?.format ?? 'integer');
  const m = { left: 34, top: 2, bottom: 34 };
  const cw = Math.max(0, (width - m.left) / 24);
  const ch = Math.max(0, Math.min(26, (height - m.top - m.bottom) / 7));
  return (
    <div className="chart" ref={ref}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={`${data.metric?.label ?? 'Orders'} by weekday and hour; peak ${fmt(max)}`}>
          {data.yLabels?.map((l: string, r: number) => (
            <text key={l} x={m.left - 6} y={m.top + r * ch + ch / 2} dy="0.32em" textAnchor="end">
              {l}
            </text>
          ))}
          {data.xLabels?.map((l: string, c: number) =>
            c % 3 === 0 ? (
              <text key={l} x={m.left + c * cw + cw / 2} y={m.top + 7 * ch + 13} textAnchor="middle">
                {l}
              </text>
            ) : null,
          )}
          {matrix.map((row, r) =>
            row.map((v, c) => (
              <rect
                key={`${r}-${c}`}
                x={m.left + c * cw + 1}
                y={m.top + r * ch + 1}
                width={Math.max(0, cw - 2)}
                height={Math.max(0, ch - 2)}
                rx={2}
                fill={step(v)}
                stroke={tip?.r === r && tip?.c === c ? 'var(--text-primary)' : 'none'}
                tabIndex={c === 0 && r === 0 ? 0 : -1}
                onPointerEnter={() => setTip({ r, c, x: m.left + c * cw, y: m.top + r * ch })}
                onPointerLeave={() => setTip(null)}
              />
            )),
          )}
          <g transform={`translate(${m.left},${m.top + 7 * ch + 20})`}>
            <text x={0} y={9}>Fewer</text>
            {STEPS.map((s, i) => (
              <rect key={s} x={40 + i * 16} y={0} width={14} height={10} rx={2} fill={`var(${s})`} />
            ))}
            <text x={40 + STEPS.length * 16 + 4} y={9}>More (max {fmt(max)})</text>
          </g>
        </svg>
      )}
      {tip && (
        <div className="tooltip" style={{ left: Math.min(tip.x + 14, width - 150), top: Math.max(0, tip.y - 34) }}>
          <div className="tt-title">
            {data.yLabels[tip.r]} {data.xLabels[tip.c]}:00 UTC
          </div>
          <div className="tt-row">
            <b>{fmt(matrix[tip.r][tip.c])}</b>
            <span className="name">{data.metric?.label}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export const heatmap: RegistryEntry = {
  component: Heatmap,
  isEmpty: (d) => !d?.matrix?.flat().some((v: number | null) => v),
  toTable: (d) => ({
    columns: [{ key: 'day', label: 'Day' }, ...(d.xLabels ?? []).map((h: string, i: number) => ({ key: `h${i}`, label: h, format: 'integer', numeric: true }))],
    rows: (d.yLabels ?? []).map((day: string, r: number) => Object.fromEntries([['day', day], ...d.matrix[r].map((v: number, i: number) => [`h${i}`, v])])),
  }),
};
